import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Plays muted looping clips only while they are on screen, and leaves them
 * paused on their poster frame when the visitor prefers reduced motion.
 *
 * @typedef {object} Refs
 * @property {HTMLVideoElement[]} [videos]
 *
 * @extends {Component<Refs>}
 */
export class VideoReel extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();
    this.#observe();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observe();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = undefined;
  }

  #observe() {
    this.#observer?.disconnect();
    this.#observer = undefined;

    const videos = this.refs.videos ?? [];
    if (videos.length === 0 || prefersReducedMotion()) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const video = entry.target;
          if (!(video instanceof HTMLVideoElement)) continue;

          if (entry.isIntersecting) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        }
      },
      { threshold: 0.25 }
    );

    for (const video of videos) {
      this.#observer.observe(video);
    }
  }
}

if (!customElements.get('video-reel')) {
  customElements.define('video-reel', VideoReel);
}
